import { useParams, Link } from 'react-router-dom'
import type { RsvpWithUser, DishGoals } from '@fellowship/shared'
import { useOccurrenceRsvps, useToggleArrived, useToggleDishArrived } from '../hooks/useChurchAdmin'
import { AdmIcn } from '../components/admin/AdminIcons'

function dishCategoryLabel(goals: DishGoals | null, key: string): string {
  return goals?.categories.find((c) => c.key === key)?.label ?? key
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function timeWindowLabel(startsAt: string): { label: string; isActive: boolean } {
  const now = Date.now()
  const start = new Date(startsAt).getTime()
  const windowOpen = start - 3 * 60 * 60 * 1000
  const windowClose = start + 6 * 60 * 60 * 1000

  if (now < windowOpen) {
    const diff = windowOpen - now
    const hrs = Math.floor(diff / 3600000)
    const mins = Math.floor((diff % 3600000) / 60000)
    return { label: `Opens in ${hrs > 0 ? `${hrs}h ` : ''}${mins}m`, isActive: false }
  }
  if (now > windowClose) {
    return { label: 'Event ended — attendance record', isActive: false }
  }
  return { label: 'Day-of mode active', isActive: true }
}

function DishArrivalSummary({ rsvps, goals }: { rsvps: RsvpWithUser[]; goals: DishGoals }) {
  const categories = goals.categories.filter((c) => c.goal > 0)

  if (categories.length === 0) return null

  function arrivedCountFor(key: string) {
    return rsvps.filter(
      (r) => r.dishCategory === key && r.dishArrivedAt !== null && r.approvalStatus !== 'declined',
    ).length
  }

  const waiting = categories.filter((cat) => arrivedCountFor(cat.key) < cat.goal)

  return (
    <div className="p-[18px] rounded-[18px] bg-surface border border-line-soft mb-4">
      <div className="eyebrow mb-3">Dish arrivals</div>
      <div className="flex flex-wrap gap-2.5">
        {categories.map((cat) => {
          const arrivedCount = arrivedCountFor(cat.key)
          const full = arrivedCount >= cat.goal
          return (
            <div
              key={cat.key}
              className="px-4 py-2.5 rounded-xl text-center min-w-[68px]"
              style={{
                background: full ? '#EEF6ED' : '#FDF5E6',
                border: `1px solid ${full ? 'rgba(74,153,62,0.2)' : 'rgba(192,130,46,0.2)'}`,
              }}
            >
              <div className="text-[20px] font-bold" style={{ color: full ? '#2D6A25' : '#8C5C0A' }}>
                {arrivedCount}/{cat.goal}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: full ? '#2D6A25' : '#8C5C0A' }}>
                {cat.label}
              </div>
            </div>
          )
        })}
      </div>
      {waiting.length > 0 && (
        <div className="mt-3 text-[12px]" style={{ color: '#8C5C0A' }}>
          Still waiting on:{' '}
          {waiting.map((cat) => `${cat.goal - arrivedCountFor(cat.key)} ${cat.label.toLowerCase()}`).join(', ')}
        </div>
      )}
    </div>
  )
}

function AttendeeRow({ rsvp, dishLabel, onToggleArrived, onToggleDishArrived, isUpdating }: {
  rsvp: RsvpWithUser
  dishLabel: string
  onToggleArrived: () => void
  onToggleDishArrived: () => void
  isUpdating: boolean
}) {
  const name = rsvp.user?.displayName ?? rsvp.guestName ?? 'Anonymous'
  const hasDish = rsvp.dishCategory !== 'none'
  const isArrived = rsvp.arrivedAt !== null
  const isDishArrived = rsvp.dishArrivedAt !== null

  return (
    <div
      className="px-4 py-3 rounded-[14px] bg-surface flex items-center gap-3"
      style={{ border: `1px solid ${isArrived ? 'rgba(74,153,62,0.3)' : 'var(--line-soft)'}` }}
    >
      <button
        onClick={onToggleArrived}
        disabled={isUpdating}
        aria-label={isArrived ? 'Mark not arrived' : 'Mark arrived'}
        aria-pressed={isArrived}
        className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-[16px] font-bold disabled:opacity-60"
        style={{
          border: isArrived ? 0 : '2px solid var(--line)',
          background: isArrived ? '#2D6A25' : 'transparent',
          color: isArrived ? '#fff' : 'var(--muted)',
          cursor: isUpdating ? 'not-allowed' : 'pointer',
        }}
      >
        {isArrived ? <AdmIcn name="check" size={16} sw={2.5} /> : '○'}
      </button>

      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-ink mb-0.5">
          {name}
          {!rsvp.userId && (
            <span className="ml-1.5 text-[11px] font-normal text-muted">guest</span>
          )}
        </div>
        {hasDish && (
          <div className="text-[12px] text-ink-2">
            {dishLabel}
            {rsvp.dishDescription ? `: ${rsvp.dishDescription}` : ''}
          </div>
        )}
      </div>

      {hasDish && (
        <button
          onClick={onToggleDishArrived}
          disabled={isUpdating}
          aria-label={isDishArrived ? 'Mark dish not arrived' : 'Mark dish arrived'}
          aria-pressed={isDishArrived}
          className="h-8 px-3 rounded-full shrink-0 text-[12px] font-medium flex items-center gap-1 disabled:opacity-60"
          style={{
            background: isDishArrived ? '#EEF6ED' : 'transparent',
            border: isDishArrived ? '1px solid rgba(74,153,62,0.3)' : '1px solid var(--line)',
            color: isDishArrived ? '#2D6A25' : 'var(--ink-2)',
            cursor: isUpdating ? 'not-allowed' : 'pointer',
          }}
        >
          {isDishArrived ? <><AdmIcn name="check" size={12} sw={2.5} /> Dish ✓</> : 'Dish?'}
        </button>
      )}
    </div>
  )
}

export default function DayOfPage() {
  const { slug, occurrenceId } = useParams<{ slug: string; occurrenceId: string }>()
  const { data, isLoading } = useOccurrenceRsvps(occurrenceId)
  const toggleArrived = useToggleArrived(occurrenceId!)
  const toggleDishArrived = useToggleDishArrived(occurrenceId!)

  const occurrence = data?.data.occurrence
  const event = data?.data.event
  const rsvps = data?.data.rsvps ?? []
  const dishGoals = event?.metadata?.dishGoals ?? null

  const attendees = rsvps.filter(
    (r) => r.attendanceStatus !== 'not_going' && r.approvalStatus !== 'declined',
  )

  const sorted = [...attendees].sort((a, b) => {
    if (a.arrivedAt && !b.arrivedAt) return -1
    if (!a.arrivedAt && b.arrivedAt) return 1
    const nameA = a.user?.displayName ?? a.guestName ?? ''
    const nameB = b.user?.displayName ?? b.guestName ?? ''
    return nameA.localeCompare(nameB)
  })

  const arrivedCount = attendees.filter((r) => r.arrivedAt !== null).length
  const stillComingCount = attendees.filter((r) => r.arrivedAt === null).length

  const timeInfo = occurrence ? timeWindowLabel(occurrence.startsAt) : null

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg font-sans">
      <div className="max-w-[680px] mx-auto px-5 pt-8 pb-16">

        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/churches/${slug}/dashboard`}
            className="inline-flex items-center gap-1.5 text-[12.5px] text-muted no-underline mb-2.5 font-medium"
          >
            ← Back to dashboard
          </Link>
          <h1 className="serif m-0 text-[40px] leading-[1.05] tracking-[-0.02em]">
            Day-of <span className="serif-i text-primary">mode.</span>
          </h1>
          {event && occurrence && (
            <div className="mt-1.5 text-[13.5px] text-ink-2">
              {event.title} · {formatDate(occurrence.startsAt)}
            </div>
          )}
        </div>

        {/* Time window indicator */}
        {timeInfo && (
          <div
            className="px-4 py-3 rounded-xl mb-4"
            style={{
              background: timeInfo.isActive ? '#EEF6ED' : 'var(--surface-2)',
              border: `1px solid ${timeInfo.isActive ? 'rgba(74,153,62,0.2)' : 'var(--line-soft)'}`,
            }}
          >
            <div
              className="text-[13px] font-medium flex items-center gap-1.5"
              style={{ color: timeInfo.isActive ? '#2D6A25' : 'var(--ink-2)' }}
            >
              <span className="text-[10px]">{timeInfo.isActive ? '●' : '○'}</span>
              {timeInfo.label}
            </div>
          </div>
        )}

        {/* Dish arrivals */}
        {dishGoals && dishGoals.categories.length > 0 && (
          <DishArrivalSummary rsvps={attendees} goals={dishGoals} />
        )}

        {/* Stat tiles */}
        <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {[
            { count: arrivedCount, label: 'Arrived', color: '#2D6A25' },
            { count: stillComingCount, label: 'Still coming', color: 'var(--ink-2)' },
            { count: attendees.length, label: 'Expected', color: 'var(--ink)' },
          ].map(({ count, label, color }) => (
            <div key={label} className="px-3 py-4 rounded-[14px] text-center bg-surface border border-line-soft">
              <div className="text-[28px] font-bold leading-none" style={{ color }}>{count}</div>
              <div className="text-[11.5px] text-muted mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Attendee list */}
        {sorted.length === 0 ? (
          <div className="px-6 py-12 rounded-[18px] text-center bg-surface border border-line-soft">
            <p className="m-0 text-[13px] text-muted">No confirmed attendees yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sorted.map((rsvp) => (
              <AttendeeRow
                key={rsvp.id}
                rsvp={rsvp}
                dishLabel={dishCategoryLabel(dishGoals, rsvp.dishCategory)}
                onToggleArrived={() => toggleArrived.mutate(rsvp.id)}
                onToggleDishArrived={() => toggleDishArrived.mutate(rsvp.id)}
                isUpdating={toggleArrived.isPending || toggleDishArrived.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
